import React from "react";
import { defineMessages, useIntl } from "react-intl";

import { HStack, Text } from "@/components/ui";
import { type Sizes } from "@/components/ui/text/text";
import { useAuth } from "@/contexts/auth/useAuth";
import { useModalContext } from "@/contexts/modal-context";
import { type Account } from "@/schemas";
import { shortNumberFormat } from "@/utils/numbers";

import { UserConnectionsTabs } from "../modals/user-connections-modal/user-connections-modal";

const messages = defineMessages({
  follows: { id: "account.follows", defaultMessage: "Following" },
});

interface FollowingButtonProps {
  account: Account;
  onClickHandler?(event: React.MouseEvent): void;
  textSize?: Sizes;
}

export default function FollowingButton(props: FollowingButtonProps) {
  const { account, onClickHandler, textSize = "md" } = props;

  const { isLoggedIn } = useAuth();

  const intl = useIntl();
  const { openModal } = useModalContext();

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    if (onClickHandler) onClickHandler(event);

    if (!isLoggedIn) {
      openModal("UNAUTHORIZED", { source: "connections" });
      return;
    }

    openModal("USER_CONNECTIONS", {
      account,
      initialTab: UserConnectionsTabs.FOLLOWING,
    });
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      title={intl.formatNumber(account.following_count)}
      className="hover:underline"
      data-testid="following-button"
    >
      <HStack alignItems="center" space={1}>
        <Text weight="bold" size={textSize}>
          {shortNumberFormat(account.following_count)}
        </Text>
        <Text theme="muted" size={textSize}>
          {intl.formatMessage(messages.follows)}
        </Text>
      </HStack>
    </button>
  );
}
